import { marked } from 'marked';

// Configure marked for safe markdown rendering
marked.setOptions({
  breaks: true, // Convert \n to <br>
  gfm: true, // GitHub Flavored Markdown
});

/**
 * Safely converts markdown text to HTML
 * @param markdownText - The markdown text to convert
 * @returns Rendered HTML string
 */
export function renderMarkdown(markdownText: string): string {
  if (!markdownText) {
    return '';
  }

  try {
    // Parse markdown to HTML
    const html = marked.parse(markdownText);
    return typeof html === 'string' ? html : '';
  } catch (error) {
    console.error('Error rendering markdown:', error);
    return markdownText; // Fallback to original text
  }
}

/**
 * Converts markdown text to plain text suitable for plain text fields
 * Removes markdown syntax while preserving formatting with line breaks and spacing
 * @param markdownText - The markdown text to convert
 * @returns Plain text string safe for plain text areas
 */
export function markdownToPlainText(markdownText: string): string {
  if (!markdownText) {
    return '';
  }

  try {
    let plainText = markdownText;

    // Remove header markers (# ## ###)
    plainText = plainText.replace(/^#{1,6}\s+/gm, '');

    // Convert bold (**text** or __text__) to plain text
    plainText = plainText.replace(/(\*\*|__)(.*?)\1/g, '$2');

    // Convert italic (*text* or _text_) to plain text
    plainText = plainText.replace(/(\*|_)(.*?)\1/g, '$2');

    // Convert inline code (`code`) to plain text
    plainText = plainText.replace(/`([^`]+)`/g, '$1');

    // Convert links [text](url) to just the text
    plainText = plainText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

    // Convert images ![alt](url) to just the alt text
    plainText = plainText.replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1');

    // Remove horizontal rules (---, ***, ___)
    plainText = plainText.replace(/^[*\-_]{3,}\s*$/gm, '');

    // Convert unordered lists (-, *, +) to simple bullets
    plainText = plainText.replace(/^[\s]*[-*+]\s+/gm, '• ');

    // Convert ordered lists (1., 2., etc.) to simple numbers
    plainText = plainText.replace(/^[\s]*\d+\.\s+/gm, (match) => {
      const indent = match.match(/^[\s]*/)?.[0] || '';
      const number = match.match(/\d+/)?.[0] || '1';
      return `${indent}${number}. `;
    });

    // Remove blockquote markers (>)
    plainText = plainText.replace(/^>\s+/gm, '');

    // Remove code block markers (``` or ~~~)
    plainText = plainText.replace(/^```[\s\S]*?\n/gm, '');
    plainText = plainText.replace(/^```\s*$/gm, '');
    plainText = plainText.replace(/^~~~[\s\S]*?\n/gm, '');
    plainText = plainText.replace(/^~~~\s*$/gm, '');

    // Clean up excessive blank lines (more than 2 consecutive)
    plainText = plainText.replace(/\n{3,}/g, '\n\n');

    // Trim whitespace from start and end
    plainText = plainText.trim();

    return plainText;
  } catch (error) {
    console.error('Error converting markdown to plain text:', error);
    return markdownText; // Fallback to original text
  }
}
